import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import React from "react";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  message?: string;
};

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      message: error?.message,
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Error renderizando la app:", error, info);
  }

  componentDidMount() {
    window.addEventListener("error", this.handleRuntimeError);
    window.addEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this.handleRuntimeError);
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection,
    );
  }

  private handleRuntimeError = (event: ErrorEvent) => {
    console.error("Error runtime no capturado:", event.error || event.message);
    this.setState({
      hasError: true,
      message: event.error?.message || event.message,
    });
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    console.error("Promesa rechazada no capturada:", reason);
    this.setState({
      hasError: true,
      message: reason instanceof Error ? reason.message : String(reason),
    });
  };

  private reload = () => {
    window.location.reload();
  };

  private goToLogin = () => {
    window.location.assign("/login");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 3,
        }}
      >
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 480 }}>
          <Alert severity="error">
            No pudimos cargar esta pantalla correctamente.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Intenta actualizar. Si vuelve a pasar, inicia sesion nuevamente.
          </Typography>
          {this.state.message && (
            <Typography variant="caption" color="text.secondary">
              Detalle tecnico: {this.state.message}
            </Typography>
          )}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={this.reload}>
              Actualizar
            </Button>
            <Button variant="outlined" onClick={this.goToLogin}>
              Ir al login
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }
}
